"use client";

import React, { Fragment, useCallback, useEffect, useRef } from "react";
import { AnimatePresence } from "motion/react";
import { motion } from "motion/react";
import { useIsMobile } from "@erp/shared-libs/hooks";
import { useOptimizeTooltipStore, type OptimizeTooltipState } from "@erp/shared-libs/stores";

const OFFSET = 5;

const getPosition = (
  content?: HTMLElement | null,
  anchor?: HTMLElement | null,
  side: "top" | "bottom" | "left" | "right" = "top"
) => {
  if (!content || !anchor) {
    return { top: 0, left: 0 };
  }
  const contentWidth = content.offsetWidth ?? 0;
  const contentHeight = content.offsetHeight ?? 0;

  const top = anchor.getBoundingClientRect().top ?? 0;
  const left = anchor.getBoundingClientRect().left ?? 0;

  const width = anchor.offsetWidth ?? 0;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const rawCenterLeft = left - (contentWidth - width) / 2;
  const centerLeft = Math.max(0, Math.min(windowWidth - contentWidth, rawCenterLeft));

  switch (side) {
    case "top": {
      const showBelow = top - contentHeight < 0;
      const rawTop = showBelow ? top + anchor.offsetHeight : top - contentHeight;
      const clampedTop = Math.min(rawTop, windowHeight - contentHeight);
      return { top: clampedTop, left: centerLeft };
    }
    case "bottom": {
      const rawTop = top + anchor.offsetHeight;
      const overflowsBottom = rawTop + contentHeight > windowHeight;
      const clampedTop = overflowsBottom ? top - contentHeight : rawTop;
      return { top: clampedTop, left: centerLeft };
    }
    case "left": {
      const rawTop = top + (anchor.offsetHeight - contentHeight) / 2;
      const clampedTop = Math.max(0, Math.min(windowHeight - contentHeight, rawTop));
      return { top: clampedTop, left: left - contentWidth };
    }
    case "right": {
      const rawTop = top + (anchor.offsetHeight - contentHeight) / 2;
      const clampedTop = Math.max(0, Math.min(windowHeight - contentHeight, rawTop));
      return { top: clampedTop, left: left + width };
    }
    default: {
      const showBelow = top - contentHeight < 0;
      const rawTop = showBelow ? top + anchor.offsetHeight : top - contentHeight;
      const clampedTop = Math.min(rawTop, windowHeight - contentHeight);
      return { top: clampedTop, left: centerLeft };
    }
  }
};

interface TooltipItemProps {
  tooltip: OptimizeTooltipState;
  registerRef: (id: string, ref: HTMLDivElement | null) => void;
  onPointerUp: () => void;
  onPointerDownCapture: (e: React.PointerEvent) => void;
  onPointerLeave: () => void;
}

/**
 * 單一 tooltip 的渲染單元：負責依 anchor + content 尺寸定位，
 * 並以 ResizeObserver 觀察 content，當內容尺寸變動（例如 spinner → 實際資料表）時重算位置，
 * 避免非同步載入的 content 撐開後 tooltip 位置錯位（特別是 left / top 等依賴 content 尺寸的方向）。
 */
const TooltipItem = ({
  tooltip,
  registerRef,
  onPointerUp,
  onPointerDownCapture,
  onPointerLeave,
}: TooltipItemProps) => {
  // ===================================================
  // Hooks
  // ===================================================
  const ref = useRef<HTMLDivElement | null>(null);

  // ===================================================
  // Functions
  // ===================================================
  /**
   * 依目前 content 與 anchor 尺寸重算位置並套用到 style
   */
  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { top, left } = getPosition(el, tooltip.ref.current, tooltip.side);
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [tooltip.ref, tooltip.side]);

  // ===================================================
  // Effects
  // ===================================================
  /**
   * 觀察 content 尺寸變動 → 重新定位；
   * 同時在掛載時先定位一次，並監聽視窗縮放（影響 clamp 邊界）。
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    reposition();

    const observer = new ResizeObserver(() => reposition());
    observer.observe(el);

    window.addEventListener("resize", reposition);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", reposition);
    };
  }, [reposition]);

  // ===================================================
  // Render
  // ===================================================
  return (
    <motion.div
      id={tooltip.id}
      ref={(node) => {
        ref.current = node;
        registerRef(tooltip.id, node);
      }}
      style={{
        position: "fixed",
        padding: `${OFFSET}px`,
        zIndex: 9997,
      }}
      className="custom-tooltip"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      transition={{ duration: 0.15, ease: "easeInOut" }}
      onPointerUp={onPointerUp}
      onPointerDownCapture={onPointerDownCapture}
      onPointerLeave={onPointerLeave}
    >
      {tooltip.content}
    </motion.div>
  );
};

export const OptimizeTooltipProvider = () => {
  // ===================================================
  // Hooks
  // ===================================================
  const { tooltips, removeTooltip, clearTooltips } = useOptimizeTooltipStore();
  const refMap = React.useRef(new Map<string, HTMLDivElement | null>());
  const isMobile = useIsMobile();

  // ===================================================
  // Functions
  // ===================================================
  /**
   * 當在手機上點擊 tooltip 時，阻止事件傳播
   */
  const handleMobilePointerDownCapture = useCallback(
    (tooltip: (typeof tooltips)[number]) => (e: React.PointerEvent) => {
      if (e.target === e.currentTarget) {
        e.stopPropagation();
        removeTooltip(tooltip.id);
      }
    },
    [removeTooltip]
  );

  /**
   * 當在非手機上點擊 tooltip 時，阻止事件傳播
   * 避免在 dialog 裡面使用會觸發 dialog 關閉
   */
  const handlePointerDownCapture = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  /**
   * 當點擊 tooltip 時，關閉 tooltip
   */
  const handlePointerUpCapture = useCallback(
    (tooltip: (typeof tooltips)[number]) => () => {
      // 點擊自動關閉
      if (tooltip.autoCloseOnClick) {
        removeTooltip(tooltip.id);
      }
    },
    [removeTooltip]
  );

  /**
   * 當游標離開 tooltip 時，關閉 tooltip
   */
  const handlePointerLeave = useCallback(
    (tooltip: (typeof tooltips)[number]) => () => {
      if (isMobile) {
        return;
      }
      removeTooltip(tooltip.id);
    },
    [isMobile, removeTooltip]
  );

  // ===================================================
  // Effects
  // ===================================================
  useEffect(() => {
    const abortController = new AbortController();

    // capture phase 才能在任何 container 滾動時都能捕捉到
    window.addEventListener(
      "scroll",
      (el) => {
        // 如果滾動的是 tooltip 本身，則不關閉
        for (const [, ref] of refMap.current) {
          const tooltip = refMap.current.get(ref?.id ?? "");
          if (tooltip && tooltip.contains(el.target as Node)) {
            return;
          }
        }

        // mobile 自己有一層 guard 就不需要處理這邊了
        if (isMobile) {
          return;
        }

        clearTooltips();
      },
      {
        capture: true,
        signal: abortController.signal,
      }
    );

    return () => {
      abortController.abort();
    };
  }, [clearTooltips, isMobile]);

  /**
   * 註冊 / 移除 tooltip DOM ref（供 scroll guard 判斷滾動目標是否在 tooltip 內），
   * 並在掛載時補上 pointerEvents（定位由 TooltipItem 自行處理）。
   */
  const registerRef = useCallback((id: string, ref: HTMLDivElement | null) => {
    refMap.current.set(id, ref);
    if (ref) {
      ref.style.pointerEvents = "auto";
    }
  }, []);

  // ===================================================
  // Render
  // ===================================================
  return (
    <AnimatePresence>
      {tooltips.map((tooltip) => {
        return (
          <Fragment key={tooltip.id}>
            {isMobile && (
              <div
                className="z-9996 fixed h-screen w-screen bg-transparent"
                style={{
                  top: 0,
                  left: 0,
                  pointerEvents: "auto", // 避免在 dialog 裡面使用會被 radix guard 擋住
                }}
                onPointerDownCapture={handleMobilePointerDownCapture(tooltip)}
              ></div>
            )}
            <TooltipItem
              tooltip={tooltip}
              registerRef={registerRef}
              onPointerUp={handlePointerUpCapture(tooltip)}
              onPointerDownCapture={handlePointerDownCapture}
              onPointerLeave={handlePointerLeave(tooltip)}
            />
          </Fragment>
        );
      })}
    </AnimatePresence>
  );
};
